/**
 * Home action-first della commessa.
 *
 * L'ordine visivo e' normativo: contesto, una sola prossima azione, blocchi
 * con deep link, lifecycle in sola lettura, metriche (assente non e' zero),
 * ultimi eventi. Offline si legge lo snapshot e non si muta niente.
 */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { readAudit, type AuditEvent } from '../admin/audit'
import { ServiceError } from '../api'
import {
  AUDIT_READ,
  EXPORT_COMPOSE,
  FINDING_MANUAL,
  INGEST_READ,
  INGEST_RETRY,
  QUALITY_PROGRESS_READ,
  QUALITY_SAMPLE,
  QUALITY_SEAL,
  REVIEW_FREEZE,
  REVIEW_QUEUE,
  type Whoami,
} from '../auth'
import { EmptyState } from '../carta/EmptyState'
import { Hint } from '../carta/Hint'
import { LineMap } from '../carta/LineMap'
import '../carta/screen.css'
import { Levels } from '../design/indicators'
import { FlightPromises } from '../FlightPromises'
import { QueueRow, QueueTable } from '../carta/QueueTable'
import { StatusPill, type PillTone } from '../carta/StatusPill'
import { stato } from '../InspectionList'
import { day, decimal, moment, notMeasured } from '../format'
import { tx } from '../i18n/t'
import { whoCan, type Concept } from '../i18n/explain'
import { useT } from '../i18n/LocaleProvider'
import { commesse, lifecycle } from '../inspections'
import { countName, stageName, stageNote } from '../lifecycleNames'
import { readProgress, type QualityProgress, type QualitySeal, type ReviewFreeze } from '../quality'
import { readSnapshot, writeSnapshot } from '../readSnapshots'
import type { Commessa, Lifecycle, Stage } from '../types'
import { ingestPath, receiptPath } from './InspectionLayout'

export const PANORAMICA = 'modulo-panoramica'

export interface InspectionOverviewProps {
  inspectionId: string
  who: Whoami
}

interface OverviewSnapshot {
  commessa: Commessa
  progress: QualityProgress | null
  lifecycle: Lifecycle | null
  freeze: ReviewFreeze | null
  seal: QualitySeal | null
  events: AuditEvent[]
}

interface Primary {
  href: string | null
  label: string | null
  reason: string
}

interface Blocker {
  href: string
  name: string
  /** La parola del dizionario che la riga nomina, se ne nomina una (GI-281). */
  about?: Concept
}

function may(who: Whoami, action: string): boolean {
  return who.may.includes(action)
}

function mayFamily(who: Whoami, prefix: string): boolean {
  return who.may.some((one) => one === prefix || one.startsWith(`${prefix}.`))
}

function canUpload(who: Whoami): boolean {
  return who.may.some((one) => one.startsWith('upload.') || one === 'package.upload')
}

function ingestActive(commessa: Commessa): boolean {
  return commessa.acquisition === 'failed' || commessa.acquisition === 'running' || commessa.acquisition === 'received'
}

function sampleIncomplete(progress: QualityProgress): boolean {
  if (progress.sample_id === null) return true
  if (progress.sample_required === null || progress.sample_reviewed === null) return false
  return progress.sample_reviewed < progress.sample_required
}

export function qualityHref(who: Whoami, inspectionId: string): string {
  return may(who, QUALITY_SAMPLE)
    ? `/inspections/${inspectionId}/quality`
    : `/inspections/${inspectionId}/queue`
}

function failedPhaseName(commessa: Commessa, stages: Lifecycle | null): string | null {
  if (commessa.job_id === null || commessa.job_id === undefined || commessa.job_id === '') {
    return null
  }
  const failed = stages?.stages.find((one) => one.state === 'failed')
  if (commessa.acquisition !== 'failed' && failed === undefined) return null
  return commessa.phase ?? failed?.phases[0] ?? failed?.name ?? null
}

function blockersOf(
  inspectionId: string,
  commessa: Commessa,
  progress: QualityProgress | null,
  who: Whoami,
  stages: Lifecycle | null,
): Blocker[] {
  const found: Blocker[] = []
  const phase = failedPhaseName(commessa, stages)
  if (phase !== null && commessa.job_id && may(who, INGEST_READ)) {
    found.push({
      href: ingestPath(commessa.job_id, inspectionId),
      name: tx('overview.phaseFailed', { phase }),
    })
  }
  if (progress === null) return found
  if (progress.conflicts > 0 && mayFamily(who, 'review')) {
    found.push({
      href: `/inspections/${inspectionId}/queue`,
      name: progress.conflicts === 1 ? tx('overview.conflictOne') : tx('overview.conflicts', { n: progress.conflicts }),
    })
  }
  if (progress.coverage_gaps > 0 && (mayFamily(who, 'review') || may(who, FINDING_MANUAL))) {
    found.push({
      href: `/inspections/${inspectionId}/coverage`,
      name:
        progress.coverage_gaps === 1
          ? tx('overview.gapOne')
          : tx('overview.gapsCount', { n: progress.coverage_gaps }),
    })
  }
  if (progress.invalid_findings > 0 && (mayFamily(who, 'review') || may(who, QUALITY_SAMPLE))) {
    found.push({
      href: qualityHref(who, inspectionId),
      name:
        progress.invalid_findings === 1
          ? tx('overview.invalidOne')
          : tx('overview.invalids', { n: progress.invalid_findings }),
    })
  }
  if (progress.active_sessions > 0 && mayFamily(who, 'review')) {
    found.push({
      href: `/inspections/${inspectionId}/queue`,
      name: progress.active_sessions === 1 ? tx('overview.sessionOne') : tx('overview.sessions', { n: progress.active_sessions }),
    })
  }
  if (progress.active_leases > 0 && mayFamily(who, 'review')) {
    found.push({
      href: `/inspections/${inspectionId}/queue`,
      name: progress.active_leases === 1 ? tx('overview.leaseOne') : tx('overview.leases', { n: progress.active_leases }),
      about: 'lease',
    })
  }
  if (progress.pending_intents > 0 && mayFamily(who, 'review')) {
    found.push({
      href: `/inspections/${inspectionId}/queue`,
      name:
        progress.pending_intents === 1
          ? tx('overview.intentOne')
          : tx('overview.intents', { n: progress.pending_intents }),
      about: 'intent',
    })
  }
  return found
}

function freezeBlocked(progress: QualityProgress | null): boolean {
  if (progress === null) return false
  return (
    progress.conflicts > 0 ||
    progress.coverage_gaps > 0 ||
    progress.invalid_findings > 0 ||
    progress.active_sessions > 0 ||
    progress.active_leases > 0 ||
    progress.pending_intents > 0
  )
}

function acqReason(commessa: Commessa): string {
  return commessa.phase
    ? tx('overview.acqStatePhase', { state: commessa.acquisition ?? '', phase: commessa.phase })
    : tx('overview.acqState', { state: commessa.acquisition ?? '' })
}

function unmatchedStep(
  commessa: Commessa,
  progress: QualityProgress | null,
): { reason: string; need: string } {
  if (ingestActive(commessa)) {
    return {
      reason: acqReason(commessa),
      need: 'ingest.retry',
    }
  }
  if (freezeBlocked(progress) && progress !== null) {
    if (progress.invalid_findings > 0 && progress.conflicts === 0 && progress.coverage_gaps === 0) {
      return {
        reason: tx('overview.reasonInvalid'),
        need: 'quality.sample',
      }
    }
    return {
      reason: tx('overview.reasonBlocks'),
      need: 'review.queue',
    }
  }
  if (progress !== null && !progress.frozen) {
    return { reason: tx('overview.reasonOpen'), need: 'review.freeze' }
  }
  if (progress !== null && progress.frozen && sampleIncomplete(progress)) {
    return { reason: tx('overview.reasonSample'), need: 'quality.sample' }
  }
  if (progress !== null && progress.frozen && (!progress.seal_id || !progress.seal_valid)) {
    return { reason: tx('overview.reasonSeal'), need: 'quality.seal' }
  }
  if (progress !== null && progress.seal_valid) {
    return { reason: tx('overview.reasonCompose'), need: 'export.compose' }
  }
  const waiting = progress?.waiting ?? commessa.waiting
  if (waiting > 0) {
    return { reason: tx('overview.reasonWaiting', { n: waiting }), need: 'review.queue' }
  }
  return { reason: tx('overview.reasonHeld'), need: 'review.freeze' }
}

export function choosePrimary(
  who: Whoami,
  inspectionId: string,
  commessa: Commessa,
  progress: QualityProgress | null,
): Primary {
  if (ingestActive(commessa) && (may(who, INGEST_RETRY) || canUpload(who))) {
    if (may(who, INGEST_RETRY) && commessa.job_id) {
      return {
        href: ingestPath(commessa.job_id, inspectionId),
        label: tx('overview.resumeIngest'),
        reason: acqReason(commessa),
      }
    }
    if (canUpload(who) && commessa.upload_id) {
      return {
        href: receiptPath(commessa.upload_id, inspectionId),
        label: tx('upload.openReceipt'),
        reason: acqReason(commessa),
      }
    }
    return {
      href: null,
      label: null,
      reason: tx('overview.ingestMissingLink'),
    }
  }

  if (freezeBlocked(progress) && mayFamily(who, 'review') && progress !== null) {
    if (progress.conflicts > 0) {
      return {
        href: `/inspections/${inspectionId}/queue`,
        label: tx('overview.openConflicts'),
        reason: tx('overview.reasonConflicts'),
      }
    }
    if (progress.coverage_gaps > 0) {
      return {
        href: `/inspections/${inspectionId}/coverage`,
        label: tx('overview.openGaps'),
        reason: tx('overview.reasonGaps'),
      }
    }
    if (progress.invalid_findings > 0) {
      return {
        href: qualityHref(who, inspectionId),
        label: tx('overview.openInvalid'),
        reason: tx('overview.reasonInvalid'),
      }
    }
    return {
      href: `/inspections/${inspectionId}/queue`,
      label: tx('pool.openQueue'),
      reason: tx('overview.reasonLive'),
    }
  }

  if (progress !== null && !progress.frozen && may(who, REVIEW_FREEZE)) {
    return {
      href: `/inspections/${inspectionId}/quality`,
      label: tx('overview.freezeBase'),
      reason: tx('overview.reasonOpen'),
    }
  }

  if (progress !== null && progress.frozen && sampleIncomplete(progress) && may(who, QUALITY_SAMPLE)) {
    return {
      href: `/inspections/${inspectionId}/quality`,
      label: tx('overview.openSample'),
      reason: tx('overview.reasonSample'),
    }
  }

  if (
    progress !== null &&
    progress.frozen &&
    (!progress.seal_id || !progress.seal_valid) &&
    may(who, QUALITY_SEAL)
  ) {
    return {
      href: `/inspections/${inspectionId}/quality`,
      label: tx('overview.openQuality'),
      reason: tx('overview.reasonSeal'),
    }
  }

  if (progress !== null && progress.seal_valid && may(who, EXPORT_COMPOSE)) {
    return {
      href: `/inspections/${inspectionId}/delivery`,
      label: tx('overview.openDelivery'),
      reason: tx('overview.reasonCompose'),
    }
  }

  const waiting = progress?.waiting ?? commessa.waiting
  if (waiting > 0 && may(who, REVIEW_QUEUE)) {
    return {
      href: `/inspections/${inspectionId}/queue`,
      label: tx('pool.openQueue'),
      reason: tx('overview.reasonWaiting', { n: waiting }),
    }
  }

  const unmatched = unmatchedStep(commessa, progress)
  return {
    href: null,
    label: null,
    // La causa e chi la scioglie, non il codice del permesso: «serve
    // quality.sample» dice a chi legge una cosa su cui non puo' agire, mentre
    // «il campione non e' completo — serve un supervisore» dice sia perche'
    // sia a chi bussare.
    reason: `${unmatched.reason} ${tx('overview.reasonOthers', {
      who: whoCan(tx, unmatched.need),
    })}`,
  }
}

function settled<T>(result: PromiseSettledResult<T>): T | null {
  return result.status === 'fulfilled' ? result.value : null
}

export function InspectionOverview({ inspectionId, who }: InspectionOverviewProps): JSX.Element {
  const t = useT()
  const [loaded, setLoaded] = useState<OverviewSnapshot | null>(null)
  const [snapshotAt, setSnapshotAt] = useState<string | null>(null)
  const [failure, setFailure] = useState<ServiceError | null>(null)
  const [empty, setEmpty] = useState(false)
  const [online, setOnline] = useState(window.navigator.onLine)
  const heading = useRef<HTMLHeadingElement | null>(null)

  useEffect(() => {
    const onState = (): void => setOnline(window.navigator.onLine)
    window.addEventListener('online', onState)
    window.addEventListener('offline', onState)
    return () => {
      window.removeEventListener('online', onState)
      window.removeEventListener('offline', onState)
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function fromNetwork(): Promise<OverviewSnapshot | 'empty'> {
      const rows = await commesse()
      const commessa = rows.find((one) => one.inspection_id === inspectionId) ?? null
      if (commessa === null) return 'empty'

      const canProgress = may(who, QUALITY_PROGRESS_READ)
      const canAudit = may(who, AUDIT_READ)
      const [progressResult, stagesResult, eventsResult] = await Promise.allSettled([
        canProgress ? readProgress(inspectionId) : Promise.resolve(null),
        canProgress ? lifecycle(inspectionId) : Promise.resolve(null),
        canAudit
          ? readAudit({
              since: '',
              until: '',
              actor: '',
              action: '',
              subject_type: 'inspection',
              subject_id: inspectionId,
              page: 1,
              size: 8,
            })
          : Promise.resolve(null),
      ])

      const progress = settled(progressResult)
      const stages = settled(stagesResult)
      const auditPage = settled(eventsResult)

      return {
        commessa,
        progress,
        lifecycle: stages,
        freeze: null,
        seal: null,
        events: auditPage?.events ?? [],
      }
    }

    async function load(): Promise<void> {
      setFailure(null)
      setEmpty(false)
      if (!window.navigator.onLine) {
        const snapshot = await readSnapshot<OverviewSnapshot>('inspection-overview', inspectionId).catch(
          () => null,
        )
        if (cancelled) return
        if (snapshot !== null) {
          setLoaded(snapshot.value)
          setSnapshotAt(snapshot.captured_at)
          return
        }
        setFailure(new ServiceError(0, 'offline', tx('overview.offlineNoSnapshot'), null))
        return
      }

      try {
        const found = await fromNetwork()
        if (cancelled) return
        if (found === 'empty') {
          setEmpty(true)
          setLoaded(null)
          return
        }
        setLoaded(found)
        setSnapshotAt(null)
        void writeSnapshot('inspection-overview', inspectionId, found).catch(() => undefined)
      } catch (error: unknown) {
        if (cancelled) return
        if (error instanceof ServiceError) setFailure(error)
        else setFailure(new ServiceError(0, 'unreachable', tx('common.unreachable'), null))
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [inspectionId, who])

  useEffect(() => {
    if (loaded !== null) heading.current?.focus()
  }, [loaded])

  if (failure !== null && loaded === null) {
    return (
      <main className="inspection-overview">
        <h2>{t('overview.heading')}</h2>
        <p role="alert">{failure.message}</p>
      </main>
    )
  }

  if (empty) {
    return (
      <main className="inspection-overview">
        <EmptyState title={t('overview.empty')} action={t('overview.emptyAction')}>
          <Link to="/work">{t('rail.work')}</Link>
        </EmptyState>
      </main>
    )
  }

  if (loaded === null) {
    return (
      <main className="inspection-overview">
        <p role="status">{t('overview.reading')}</p>
      </main>
    )
  }

  const { commessa, progress, lifecycle: stages } = loaded
  const primary = choosePrimary(who, inspectionId, commessa, progress)
  const blockers = blockersOf(inspectionId, commessa, progress, who, stages)
  const readonly = snapshotAt !== null || !online

  return (
    <main className="inspection-overview screen screen-passport">
      {/*
        Il passaporto era un flusso di titoli lungo due schermi e mezzo (S17):
        contesto, azione, blocchi, tappe, metriche, volo, mappa, giornale, uno
        sotto l'altro in una colonna da 66ch. Ora e' una griglia che riempie lo
        schermo: la mappa a sinistra su due righe, le tappe e le anomalie in
        mezzo, il volo a destra su tutta l'altezza, il prossimo passo con i
        blocchi e il giornale in basso. Ogni area risponde a una domanda e sta
        al suo posto; cio' che non ci sta scorre dentro l'area, mai la pagina.
      */}
      <h2 ref={heading} tabIndex={-1} className="solo-lettori">
        {t('overview.heading')}
      </h2>
      <p className="screen-strip">
        <span>
          {t('overview.job')} <strong>{commessa.external_inspection_id}</strong>
          {commessa.plant_type ? ` · ${commessa.plant_type}` : ''}
          {commessa.country ? ` · ${commessa.country}` : ''}
          {commessa.provider ? ` · ${commessa.provider}` : ''}
        </span>
        <span className="tenue">
          {commessa.activity ?? t('overview.activityUnknown')} · {day(commessa.created_at)} ·{' '}
          {t('overview.acquisition')} {commessa.acquisition === null ? notMeasured() : stato(commessa, t).label}
          {commessa.phase ? ` · ${commessa.phase}` : ''}
          {commessa.delivery ? ` · ${t('overview.deliveryWord')} ${commessa.delivery}` : ''}
        </span>
        {readonly && (
          <span role="note" className="tenue">
            {snapshotAt !== null
              ? t('overview.offlineNote', { when: moment(snapshotAt) })
              : t('overview.offlineBare')}
            <Hint about="snapshot" />
          </span>
        )}
      </p>

      <section className="screen-area area-map" aria-label={t('lineMap.title')}>
        <h2>{t('lineMap.title')}</h2>
        <LineMap inspectionId={inspectionId} />
      </section>

      <section className="screen-area area-lifecycle" aria-label={t('overview.lifecycle')}>
        <h2>{t('overview.lifecycle')}</h2>
        {stages === null ? (
          <p className="tenue">{t('overview.lifecycleMissing')}</p>
        ) : (
          <ol className="tappe-panoramica">
            {stages.stages.map((stage) => (
              <StageRow key={stage.ordinal} stage={stage} />
            ))}
          </ol>
        )}
        <p className="tenue" style={{ fontSize: 12 }}>
          {t('overview.lifecycleHint', { who: whoCan(t, 'ingest.retry') })}{' '}
          <code className="tenue">ingest.retry</code>
        </p>
      </section>

      <section className="screen-area area-flight" aria-label={t('flight.heading')}>
        <h2>{t('flight.heading')}</h2>
        {may(who, QUALITY_PROGRESS_READ) ? (
          <FlightPromises inspectionId={inspectionId} compact />
        ) : (
          <p className="tenue">{t('overview.auditNeeds', { who: whoCan(t, 'quality.progress.read') })}</p>
        )}
      </section>

      <section className="screen-area area-anomalies" aria-label={t('overview.anomalies')}>
        <h2>{t('overview.anomalies')}</h2>
        <section aria-label={t('overview.metrics')}>
          <p><Levels counts={commessa.waiting_by_level} /></p>
          <h2>{t('overview.metrics')}</h2>
          {progress === null ? (
            <p className="tenue">{t('overview.metricsMissing')}</p>
          ) : (
            <p>
              {t('overview.waiting')} <span className="t-num">{decimal(progress.waiting)}</span> ·{' '}
              {t('overview.decided')}{' '}
              <span className="t-num">{decimal(progress.decided)}</span> · {t('overview.gaps')}{' '}
              <span className="t-num">{decimal(progress.coverage_gaps)}</span> · {t('overview.sample')}{' '}
              {progress.sample_required === null || progress.sample_reviewed === null ? (
                <span className="t-num">{notMeasured()}</span>
              ) : (
                <span className="t-num">
                  {decimal(progress.sample_reviewed)} / {decimal(progress.sample_required)}
                </span>
              )}
            </p>
          )}
          {may(who, QUALITY_PROGRESS_READ) && (
            <p>
              <Link to={qualityHref(who, inspectionId)}>{t('overview.openQuality')}</Link>
            </p>
          )}
        </section>
        <p>
          <Link to={`/inspections/${inspectionId}/anomalies`}>{t('overview.openAnomalies')}</Link>
        </p>
      </section>

      <section className="screen-area area-next" aria-label={t('overview.nextAction')}>
        <h2>{t('overview.nextAction')}</h2>
        {primary.href !== null && primary.label !== null ? (
          <p>
            <Link className="azione primaria primario" to={primary.href}>
              {primary.label}
            </Link>
          </p>
        ) : null}
        <p className="tenue">{primary.reason}</p>
        <h2>{t('overview.blockers')}</h2>
        {blockers.length === 0 ? (
          <p className="tenue">{t('overview.noBlockers')}</p>
        ) : (
          <ul>
            {blockers.map((one) => (
              <li key={`${one.href}:${one.name}`}>
                <Link to={one.href}>{one.name}</Link>
                {one.about !== undefined && <Hint about={one.about} />}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="screen-area area-activity" aria-label={t('overview.activity')}>
        <h2>{t('overview.activity')}</h2>
        {may(who, AUDIT_READ) ? (
          <>
            {loaded.events.length === 0 ? (
              <p className="tenue">{t('overview.noEvents')}</p>
            ) : (
              <QueueTable>
                {loaded.events.slice(0, 6).map((one) => (
                  <QueueRow key={one.id}>
                    <span className="t-num">{moment(one.at)}</span>
                    <span>{one.action}</span>
                  </QueueRow>
                ))}
              </QueueTable>
            )}
            <p>
              <Link
                to={`/admin/audit?subject_type=inspection&subject_id=${encodeURIComponent(inspectionId)}`}
              >
                {t('overview.openAudit')}
              </Link>
            </p>
          </>
        ) : (
          <p className="tenue">
            {t('overview.auditNeeds', { who: whoCan(t, 'audit.read') })}{' '}
            <code className="tenue">audit.read</code>
          </p>
        )}
      </section>
    </main>
  )
}

const STAGE_TONE: Record<string, PillTone> = {
  queued: 'ink-3',
  running: 'ref',
  done: 'sev-0',
  failed: 'sev-4',
}

function stageLabel(state: string, t: (key: 'pool.statusQueued' | 'pool.statusProcessing' | 'ingest.phaseDone' | 'pool.statusError') => string): string {
  if (state === 'queued') return t('pool.statusQueued')
  if (state === 'running') return t('pool.statusProcessing')
  if (state === 'done') return t('ingest.phaseDone')
  if (state === 'failed') return t('pool.statusError')
  return state
}

function StageRow({ stage }: { stage: Stage }): JSX.Element {
  const t = useT()
  return (
    <li>
      <span className="t-read">{stageName(t, stage)}</span>{' '}
      <StatusPill
        label={stageLabel(stage.state, t)}
        tone={STAGE_TONE[stage.state] ?? 'ink-3'}
        pulse={stage.state === 'running'}
      />
      {stage.counts.map((one) => (
        <span key={one.what} className="tenue">
          {' '}
          · {countName(t, one)}{' '}
          <span className="t-num">{one.value === null ? notMeasured() : decimal(one.value)}</span>
        </span>
      ))}
      {stageNote(t, stage) !== null && <span className="tenue"> · {stageNote(t, stage)}</span>}
      {stage.error !== null && <p className="guasto">{stage.error}</p>}
    </li>
  )
}
